import React, { useEffect, useRef, useState, useCallback } from 'react';
import { withTranslation, useTranslation } from 'react-i18next';
import './XAdvisor.css';

/* ══════════════════════════════════════════════
   Iconos SVG internos (sin dependencia externa)
   ══════════════════════════════════════════════ */

const IconSignal = ({ color = 'currentColor' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
  </svg>
);

const IconAlert = ({ color = 'currentColor' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const IconDrop = ({ color = 'currentColor' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <path d="M12 2.69l5.66 5.66a8 8 0 11-11.31 0L12 2.69z"/>
  </svg>
);

const IconGrid = ({ color = 'currentColor' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <path d="M3 9h18M9 21V9"/>
  </svg>
);

const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

const IconSend = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="#0f1847" strokeWidth="2.5" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true">
    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
  </svg>
);

const IconUser = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
    stroke="rgba(255,255,255,0.7)" strokeWidth="2.5" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const IconBot = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="#fff" strokeWidth="2.5" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="4"/>
    <path d="M12 2v2M12 20v2M2 12h2M20 12h2"/>
  </svg>
);

const IconHint = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
    stroke="#4ade80" strokeWidth="2" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 8v4M12 16h.01"/>
  </svg>
);

/* Config estática de features — textos vienen del JSON */
const FEATURES = [
  {
    key:       'feature_1',
    iconColor: 'rgba(74,222,128,0.12)',
    Icon:      () => <IconSignal color="#4ade80" />,
  },
  {
    key:       'feature_2',
    iconColor: 'rgba(239,159,39,0.12)',
    Icon:      () => <IconAlert color="#EF9F27" />,
  },
  {
    key:       'feature_3',
    iconColor: 'rgba(55,138,221,0.12)',
    Icon:      () => <IconDrop color="#378ADD" />,
  },
  {
    key:       'feature_4',
    iconColor: 'rgba(127,119,221,0.12)',
    Icon:      () => <IconGrid color="#7F77DD" />,
  },
];

/* ══════════════════════════════════════════════
   Sub-componentes
   ══════════════════════════════════════════════ */

/* Burbuja de mensajes del chat */
const ChatMessage = ({ msg }) => (
  <div className={`xAdv-msg xAdv-msg--${msg.role}`}>
    <div className={`xAdv-msg-avatar xAdv-msg-avatar--${msg.role}`}>
      {msg.role === 'ai' ? <IconBot size={12} /> : <IconUser />}
    </div>
    <div className="xAdv-msg-content">
      <div
        className={`xAdv-msg-bubble xAdv-msg-bubble--${msg.role}`}
        dangerouslySetInnerHTML={{ __html: msg.text }}
      />
      {msg.tags && msg.tags.length > 0 && (
        <div className="xAdv-tags">
          {msg.tags.map((tag, i) => (
            <span key={i} className={`xAdv-tag xAdv-tag--${tag.type}`}>
              {tag.label}
            </span>
          ))}
        </div>
      )}
    </div>
  </div>
);

/* Indicador de escritura */
const TypingIndicator = () => (
  <div className="xAdv-msg xAdv-msg--ai">
    <div className="xAdv-msg-avatar xAdv-msg-avatar--ai">
      <IconBot size={12} />
    </div>
    <div className="xAdv-typing">
      <span className="xAdv-typing-dot" />
      <span className="xAdv-typing-dot" />
      <span className="xAdv-typing-dot" />
    </div>
  </div>
);

/* ══════════════════════════════════════════════
   Componente principal
   ══════════════════════════════════════════════ */

const XAdvisor = function XAdvisor({ data }) {
  const [t] = useTranslation('common');
  const sectionRef  = useRef(null);
  const chatBodyRef = useRef(null);
  const [visible, setVisible]   = useState(false);
  const [inputVal, setInputVal] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  // ── Textos desde common.json → xAgro.advisor ──
  const eyebrow  = t(`${data}.eyebrow`);
  const title    = t(`${data}.title`);
  const subtitle = t(`${data}.subtitle`);
  const ctaText  = t(`${data}.cta`);

  // Contexto del chat (cultivo activo y lecturas en vivo)
  const ctxCropLabel  = t(`${data}.ctx_crop_label`);
  const ctxCropValue  = t(`${data}.ctx_crop_value`);
  const ctxSoilLabel  = t(`${data}.ctx_soil_label`);
  const ctxSoilValue  = t(`${data}.ctx_soil_value`);
  const ctxTempLabel  = t(`${data}.ctx_temp_label`);
  const ctxTempValue  = t(`${data}.ctx_temp_value`);

  // Header del chat
  const chatName      = t(`${data}.chat_name`);
  const chatConnected = t(`${data}.chat_connected`);

  // Input placeholder
  const inputPlaceholder = t(`${data}.input_placeholder`);

  // Mensajes iniciales precargados desde el JSON
  const initialMessages = t(`${data}.initial_messages`, { returnObjects: true });

  // Sugerencias de preguntas
  const suggestions = t(`${data}.suggestions`, { returnObjects: true });

  const [messages, setMessages] = useState(
    Array.isArray(initialMessages) ? initialMessages : []
  );

  // IntersectionObserver para animación de entrada
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Auto-scroll al último mensaje
  useEffect(() => {
    const el = chatBodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isTyping]);

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.hash = '#contact';
    }
  };

  /* Envío de mensaje: agrega el mensaje del usuario y simula
     respuesta de la IA con un breve delay de "escritura". */
  const handleSend = useCallback(() => {
    const text = inputVal.trim();
    if (!text || isTyping) return;

    const userMsg = { role: 'user', text, tags: [] };
    setMessages((prev) => [...prev, userMsg]);
    setInputVal('');
    setIsTyping(true);

    // Simula latencia de respuesta IA (1.5 s)
    setTimeout(() => {
      const aiReply = {
        role: 'ai',
        text: t(`${data}.generic_reply`, {
          defaultValue:
            'Analizando los datos de tus sensores... Te recomendaré acciones concretas en base a las condiciones actuales de tu parcela.',
        }),
        tags: [],
      };
      setMessages((prev) => [...prev, aiReply]);
      setIsTyping(false);
    }, 1500);
  }, [inputVal, isTyping, t, data]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestion = (text) => {
    if (isTyping) return;
    setInputVal(text);
  };

  return (
    <section
      id="xAgroAdvisor"
      className={`xAdv-section ${visible ? 'xAdv-visible' : ''}`}
      ref={sectionRef}
    >
      {/* Patrón de puntos — reutiliza clase global de tokens */}
      <div className="xagro-bg-pattern" aria-hidden="true" />

      <div className="xAdv-wrapper">

        {/* ── Encabezado ── */}
        <div className="xAdv-header">
          {/* Badge — reutiliza clases globales de tokens */}
          <div className="xagro-badge xagro-badge--dark">
            <span className="xagro-badge-dot xagro-badge-dot--animated" />
            {eyebrow}
          </div>
          <h2 className="xAdv-title">{title}</h2>
          <p className="xAdv-subtitle">{subtitle}</p>
        </div>

        <div className="xAdv-layout">

          {/* ── Columna izquierda: features ── */}
          <div className="xAdv-features">
            {FEATURES.map(({ key, iconColor, Icon }, idx) => (
            <div
              key={key}
              className="xAdv-feature"
              style={{ '--xAdv-delay': `${idx * 0.1}s` }}
            >
              <div
                className="xAdv-feature-icon"
                style={{ background: iconColor }}
              >
                <Icon />
              </div>
              <div className="xAdv-feature-body">
                <span className="xAdv-feature-title">
                  {t(`${data}.${key}_title`)}
                </span>
                <span className="xAdv-feature-desc">
                  {t(`${data}.${key}_desc`)}
                </span>
              </div>
            </div>
          ))}
            <button className="xAdv-cta" onClick={scrollToContact}
             style={{ '--xAdv-delay': `${FEATURES.length * 0.1}s` }}
              >
              {ctaText}
              <IconArrow />
            </button>
          </div>

          {/* ── Columna derecha: chat ── */}
          <div className="xAdv-chat" role="region" aria-label={chatName}>

            {/* Header */}
            <div className="xAdv-chat-header">
              <div className="xAdv-chat-avatar">
                <IconBot size={14} />
              </div>
              <div>
                <span className="xAdv-chat-header-name">{chatName}</span>
                <div className="xAdv-chat-header-status">
                  {/* Dot de grabación reutiliza animación de tokens */}
                  <span
                    className="xagro-badge-dot xagro-badge-dot--animated"
                    style={{ width: 5, height: 5 }}
                  />
                  {chatConnected}
                </div>
              </div>
            </div>

            {/* Barra de contexto */}
            <div className="xAdv-context-bar">
              <div className="xAdv-context-item">
                <span className="xAdv-context-label">{ctxCropLabel}</span>
                <span className="xAdv-context-value">{ctxCropValue}</span>
              </div>
              <div className="xAdv-context-sep" />
              <div className="xAdv-context-item">
                <span className="xAdv-context-label">{ctxSoilLabel}</span>
                <span className="xAdv-context-value">{ctxSoilValue}</span>
              </div>
              <div className="xAdv-context-sep" />
              <div className="xAdv-context-item">
                <span className="xAdv-context-label">{ctxTempLabel}</span>
                <span className="xAdv-context-value">{ctxTempValue}</span>
              </div>
            </div>

            {/* Mensajes */}
            <div className="xAdv-chat-body" ref={chatBodyRef}>
              {messages.map((msg, i) => (
                <ChatMessage key={i} msg={msg} />
              ))}
              {isTyping && <TypingIndicator />}
            </div>

            {/* Sugerencias */}
            {Array.isArray(suggestions) && suggestions.length > 0 && (
              <div className="xAdv-suggestions">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    className="xAdv-suggestion"
                    onClick={() => handleSuggestion(s)}
                    disabled={isTyping}
                  >
                    <IconHint />
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="xAdv-chat-input">
              <input
                className="xAdv-input"
                type="text"
                placeholder={inputPlaceholder}
                value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isTyping}
                aria-label={inputPlaceholder}
              />
              <button
                className="xAdv-send-btn"
                onClick={handleSend}
                disabled={isTyping || !inputVal.trim()}
                aria-label="Enviar mensaje"
              >
                <IconSend />
              </button>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default withTranslation('common')(XAdvisor);